import React, { FC, useEffect, useState } from 'react';
import { Navigate, useNavigate, useLocation } from 'react-router-dom';
import { AuthenticationType } from 'api/public';

import { useGlobalClient } from 'hooks';
import cookieStorage from 'cookie-storage';
import * as paths from 'paths';

import SignIn from './SignIn';
import SignUp from './SignUp';
import ForgotPassword from './ForgotPassword';
import ForgotPasswordSubmit from './ForgotPasswordSubmit';
import ConfirmSignUp from './ConfirmSignUp';
import Invitation from './Invitation';

import { AuthContainerProps } from '../types';
import Passwordless from './Passwordless';

const SSO_ERROR_PREFIX = 'Error in SAML response processing: PreSignUp failed with error ';

const AuthController: FC = () => {
  const [password, setPassword] = useState('');
  const { pathname } = useLocation();

  const navigate = useNavigate();

  const client = useGlobalClient();
  const { sso_configuration, authentication_type } = client;
  const clientIsSSO = authentication_type === AuthenticationType.Sso && sso_configuration;
  const clientIsPasswordless = authentication_type === AuthenticationType.Passwordless;
  const urlParams = new URLSearchParams(window.location.search);
  const ssoError = urlParams.get('error_description');

  useEffect(() => {
    if (ssoError) {
      let authError = ssoError;
      if (authError.startsWith(SSO_ERROR_PREFIX)) {
        authError = authError.split(SSO_ERROR_PREFIX)[1];
        authError = authError.substring(0, authError.length - 2);
      }
      sessionStorage.setItem('authError', authError);
      navigate(paths.authError, { replace: true });
    }
  }, [ssoError]);

  if (!ssoError && clientIsSSO) {
    return <Navigate to={paths.sso} replace />;
  }

  const mainProps: AuthContainerProps = {
    authData: {
      username: cookieStorage.userEmail,
      password,
    },
    updateUsername(newUserName: string) {
      cookieStorage.userEmail = newUserName;
    },
    updatePassword(newPassword: string) {
      setPassword(newPassword);
    },
  };

  let Main: FC<AuthContainerProps> = null;

  switch (pathname) {
    case paths.login:
      Main = clientIsPasswordless ? Passwordless : SignIn;
      break;
    case paths.passwordLogin:
      Main = SignIn;
      break;
    case paths.passwordlessLogin:
      Main = Passwordless;
      break;
    case paths.signUp:
      Main = SignUp;
      break;
    case paths.forgotPassword:
      Main = ForgotPassword;
      break;
    case paths.forgotPasswordSubmit:
      Main = ForgotPasswordSubmit;
      break;
    case paths.confirmSignup:
      Main = ConfirmSignUp;
      break;
    case paths.invitation:
      Main = clientIsPasswordless ? Passwordless : Invitation;
      break;
    default:
      Main = clientIsPasswordless ? Passwordless : SignIn;
  }

  return <Main {...mainProps} />;
};

export default AuthController;
